import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { SHOW_ALL_ITEMS_QUANTITY } from '@/shared/config';
import {
  trainingContentApi,
  TRAINING_CONTENT_EXTENSION_OPTIONS,
  TRAINING_CONTENT_TYPE_OPTIONS,
  type TrainingContentExtension,
  type TrainingContentFilters,
  type TrainingContentSortField,
  type TrainingContentSortOrder,
  type TrainingContentType,
} from '@/entities/Training';
import { CONTENT_ORDER_OPTIONS } from './useTrainingContentsTable';
import { INFINITE_SCROLL_PAGE_SIZE, usePaginatedListFetch } from './usePaginatedListFetch';

const DEFAULT_ITEMS_PER_PAGE = 10;

const ITEMS_PER_PAGE_OPTIONS = new Set([10, 25, 50, SHOW_ALL_ITEMS_QUANTITY]);

const DEFAULT_SORT: TrainingContentSortField = 'createdAt';
const DEFAULT_ORDER: TrainingContentSortOrder = 'desc';

const ERROR_MESSAGE = 'Não foi possível carregar os conteúdos. Tente novamente.';

interface ResolvedOrderOption {
  sort: TrainingContentSortField | '';
  order: TrainingContentSortOrder | '';
}

const NO_ORDER_SELECTED: ResolvedOrderOption = { sort: '', order: '' };

function getQueryString(value: unknown): string {
  if (Array.isArray(value)) return typeof value[0] === 'string' ? value[0] : '';
  return typeof value === 'string' ? value : '';
}

const CIVIL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function parsePositiveInteger(value: unknown): number | undefined {
  const parsed = Number(getQueryString(value));
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : undefined;
}

function parseEnumValue<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  const parsed = getQueryString(value);
  return allowed.includes(parsed as T) ? (parsed as T) : undefined;
}

function parseCivilDateQuery(value: unknown): string | undefined {
  const parsed = getQueryString(value);
  return CIVIL_DATE_PATTERN.test(parsed) ? parsed : undefined;
}

function cleanFilters(filters: TrainingContentFilters): TrainingContentFilters {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== null && value !== '')
  ) as TrainingContentFilters;
}

function resolveOrderOption(sort: string, order: string): ResolvedOrderOption {
  const matched = CONTENT_ORDER_OPTIONS.find(
    (option) => option.sort === sort && option.order === order
  );

  if (!matched) return NO_ORDER_SELECTED;

  return {
    sort: matched.sort as TrainingContentSortField,
    order: matched.order as TrainingContentSortOrder,
  };
}

export function useTrainingContentsSearch() {
  const route = useRoute();
  const router = useRouter();

  const term = ref('');
  const sort = ref<TrainingContentSortField | ''>('');
  const order = ref<TrainingContentSortOrder | ''>('');
  const page = ref(1);
  const itemsPerPage = ref(DEFAULT_ITEMS_PER_PAGE);
  const filters = ref<TrainingContentFilters>({});
  const hasHydratedFromUrl = ref(false);

  const isInfiniteScrollMode = computed(() => itemsPerPage.value === SHOW_ALL_ITEMS_QUANTITY);

  const hasActiveFilters = computed(() => Object.keys(filters.value).length > 0);

  const hasActiveSearch = computed(() => term.value.trim().length > 0 || hasActiveFilters.value);

  function hydrateFromQuery() {
    const query = route.query;

    page.value = Math.max(1, Number(query.page) || 1);

    const requestedItemsPerPage = Number(query.itemsPerPage);
    itemsPerPage.value = ITEMS_PER_PAGE_OPTIONS.has(requestedItemsPerPage)
      ? requestedItemsPerPage
      : DEFAULT_ITEMS_PER_PAGE;

    term.value = getQueryString(query.term).trim();

    const hydratedOrder = resolveOrderOption(
      getQueryString(query.sort),
      getQueryString(query.order)
    );
    sort.value = hydratedOrder.sort;
    order.value = hydratedOrder.order;

    filters.value = cleanFilters({
      extension: parseEnumValue<TrainingContentExtension>(
        query.extension,
        TRAINING_CONTENT_EXTENSION_OPTIONS.map((option) => option.value)
      ),
      contentType: parseEnumValue<TrainingContentType>(
        query.contentType,
        TRAINING_CONTENT_TYPE_OPTIONS.map((option) => option.value)
      ),
      responsibleMemberId: parsePositiveInteger(query.responsibleMemberId),
      responsiblePositionId: parsePositiveInteger(query.responsiblePositionId),
      createdAt: parseCivilDateQuery(query.createdAt),
    });

    hasHydratedFromUrl.value = true;
  }

  function buildQueryFromState(): Record<string, string> {
    const query: Record<string, string> = {};
    if (term.value) query.term = term.value;
    if (sort.value && order.value) {
      query.sort = sort.value;
      query.order = order.value;
    }
    if (page.value > 1) query.page = String(page.value);
    if (itemsPerPage.value !== DEFAULT_ITEMS_PER_PAGE) {
      query.itemsPerPage = String(itemsPerPage.value);
    }
    for (const [key, value] of Object.entries(filters.value)) {
      query[key] = String(value);
    }
    return query;
  }

  function syncUrl() {
    if (!hasHydratedFromUrl.value) return;
    router.replace({ path: route.path, query: buildQueryFromState() });
  }

  hydrateFromQuery();

  function requestPage(targetPage: number) {
    return trainingContentApi.listTrainingContents({
      page: targetPage,
      itemsPerPage: isInfiniteScrollMode.value
        ? INFINITE_SCROLL_PAGE_SIZE
        : Number(itemsPerPage.value),
      sort: sort.value || DEFAULT_SORT,
      order: order.value || DEFAULT_ORDER,
      ...(term.value ? { term: term.value } : {}),
      ...filters.value,
    });
  }

  const {
    items,
    metadata,
    loading,
    loadingMore,
    hasMoreItems,
    error,
    isInitialLoading,
    fetch,
    loadMore,
  } = usePaginatedListFetch({
    page,
    isInfiniteScrollMode,
    errorMessage: ERROR_MESSAGE,
    requestPage,
  });

  const hasContents = computed(() => items.value.length > 0);

  const showControls = computed(() => hasContents.value || hasActiveSearch.value);

  const isEmptyResult = computed(() => !hasContents.value && hasActiveSearch.value);

  function changePage(newPage: number) {
    page.value = newPage;
  }

  function changeItemsPerPage(newItemsPerPage: number) {
    itemsPerPage.value = newItemsPerPage;
    page.value = 1;
  }

  function handleSearch(newTerm: string) {
    const trimmed = newTerm.trim();
    if (trimmed === term.value) return;
    term.value = trimmed;
    page.value = 1;
  }

  function applyFilters(newFilters: TrainingContentFilters) {
    filters.value = cleanFilters(newFilters);
    page.value = 1;
  }

  function clearFilters() {
    filters.value = {};
    page.value = 1;
  }

  function handleSortUpdate(newSort: string, newOrder: string) {
    const selected = resolveOrderOption(newSort, newOrder);
    sort.value = selected.sort;
    order.value = selected.order;
    page.value = 1;
  }

  watch(
    [page, itemsPerPage, term, sort, order, filters],
    () => {
      syncUrl();
      fetch();
    },
    { immediate: true, deep: true }
  );

  return {
    items,
    metadata,
    loading,
    loadingMore,
    hasMoreItems,
    isInitialLoading,
    isInfiniteScrollMode,
    error,
    term,
    sort,
    order,
    page,
    itemsPerPage,
    filters,
    hasContents,
    hasActiveSearch,
    hasActiveFilters,
    showControls,
    isEmptyResult,
    changePage,
    changeItemsPerPage,
    handleSearch,
    handleSortUpdate,
    applyFilters,
    clearFilters,
    loadMore,
    fetch,
  };
}
